/**
 * IL VOCABOLARIO DEI CONNETTORI — i codici del BE e come si leggono a schermo.
 *
 * Il dominio sta in `Src/MIT.Fwk.Infrastructure/Entities/Integrations/` del repo BE, nato il
 * 24-08-2026 come fase T1+T2 del piano di fatturazione verso i gestionali esterni
 * (`docs/contract/BE-PLAN-FATTURAZIONE-E-CONNETTORE-v1.md`). Un **connettore** non è un tipo di
 * integrazione: è l'istanza registrata di un agente installato nella rete del cliente, che parla
 * con il suo gestionale (oggi il solo previsto è Alyante) e scrive qui esecuzioni, log e coda.
 *
 * ⚠️ `integrations` (la risorsa) **non c'entra**: è il registro delle applicazioni autorizzate al
 * login, globale e legacy. Stesso nome inglese, concetto diverso.
 *
 * I codici sono le costanti delle entità (`Connector.Statuses`, `ConnectorRun.Statuses`, …): un
 * codice che il BE aggiunge senza passare di qui si mostra com'è, invece di sparire.
 */

export const CONNECTOR_STATUS_LABEL: Record<string, string> = {
  unknown: 'Mai sentito',
  healthy: 'Attivo',
  stale: 'Silenzioso',
  error: 'In errore',
};

export const TOPOLOGY_LABEL: Record<string, string> = {
  cloud: 'Cloud',
  on_premise: 'Presso il cliente',
};

export const RUN_STATUS_LABEL: Record<string, string> = {
  running: 'In corso',
  completed: 'Completata',
  failed: 'Fallita',
  cancelled: 'Annullata',
};

export const RUN_TRIGGER_LABEL: Record<string, string> = {
  cron: 'Pianificata',
  manual: 'Manuale',
  command: 'Da comando',
};

export const COMMAND_STATUS_LABEL: Record<string, string> = {
  queued: 'In coda',
  claimed: 'Preso in carico',
  done: 'Eseguito',
  failed: 'Fallito',
  cancelled: 'Annullato',
};

export const COMMAND_OPERATION_LABEL: Record<string, string> = {
  'run.now': 'Sincronizza ora',
  'bind.initial': 'Aggancio iniziale',
  'cleanup.import-leftovers': 'Pulizia dei residui dell’import',
  'invite.activate': 'Invito delle persone',
  rollback: 'Annulla l’invio',
  'resync.master-data': 'Riallinea le anagrafiche',
};

export const OUTBOX_STATUS_LABEL: Record<string, string> = {
  queued: 'In coda',
  sending: 'In invio',
  sent: 'Inviato',
  acknowledged: 'Confermato',
  failed: 'Fallito',
  cancelled: 'Annullato',
};

export const OUTBOX_DIRECTION_LABEL: Record<string, string> = {
  out: 'Verso il gestionale',
  in: 'Dal gestionale',
};

/** L'etichetta di un codice, o il codice stesso se il vocabolario non lo conosce. */
export function labelOf(map: Record<string, string>, code: string | null | undefined): string {
  if (!code) return '—';
  return map[code] ?? code;
}

/** Durata leggibile da millisecondi, come nella console dei job. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${Math.round(s % 60)} s`;
}

/**
 * Da quanto tempo il connettore non si fa sentire. Il BE deriva `status = stale` dal battito, ma
 * il numero dice quanto: «silenzioso da 3 minuti» e «da 4 giorni» non sono lo stesso problema.
 */
export function heartbeatAge(lastHeartbeatAt: string | null | undefined, now: Date = new Date()): string {
  if (!lastHeartbeatAt) return 'mai';
  const diffMs = now.getTime() - new Date(lastHeartbeatAt).getTime();
  if (Number.isNaN(diffMs)) return '—';
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'adesso';
  if (min < 60) return `${min} min fa`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h fa`;
  return `${Math.round(h / 24)} giorni fa`;
}

/** `maespace` è la scheda dell'integrazione MaeSpace: solo super-admin, la filtra la console. */
export type ConnectorsTab = 'connectors' | 'runs' | 'commands' | 'outbox' | 'maespace';

export const CONNECTORS_TABS: readonly { value: ConnectorsTab; label: string }[] = [
  { value: 'connectors', label: 'Connettori' },
  { value: 'runs', label: 'Esecuzioni' },
  { value: 'commands', label: 'Comandi' },
  { value: 'outbox', label: 'Coda d’invio' },
  { value: 'maespace', label: 'MaeSpace' },
];

export function isConnectorsTab(v: unknown): v is ConnectorsTab {
  return typeof v === 'string' && CONNECTORS_TABS.some((t) => t.value === v);
}
